import JsEvent from '@rotomeca/event';
import { EMPTY_STRING, Nullable } from '@rotomeca/utils';
import BnumElement, { CustomDom } from '../../bnum-element';
import { TAG_ICON } from '../../../core/utils/constants/tags';
import { Scheduler } from '../../../core/utils/Scheduler';
import BnumElementInternal from '../../bnum-element-states';
import { Define, Self } from '../../../core/decorators';
import { Listener } from 'core/decorators/listener';
import { OnIconChangeInitializer } from './bnum-icon.internal/listeners';
import { DATA_ICON } from './bnum-icon.internal/constants';
import symbols from '../../../core/styles/material-symbols.css';
import style from './bnum-icon.less';

/**
 * Classe CSS utilisée pour les icônes Material Symbols.
 */
const ICON_CLASS = 'material-symbols-outlined';

/**
 * Source de la police, extraite du même CSS que celui utilisé pour le rendu
 * (source unique) plutôt que dupliquée en dur ici.
 */
const FONT_SRC =
	symbols.match(/url\(([^)]+)\)/)?.[1] ??
	'./skins/mel_elastic/fonts/material-symbol-v2.woff2';

/**
 * Feuille de style CSS pour les icônes Material Symbols.
 */
const SYMBOLS = BnumElement.ConstructCSSStyleSheet(
	symbols
		.replaceAll(`.${ICON_CLASS}`, ':host')
		.replaceAll('fonts/material-symbol-v2.woff2', FONT_SRC),
);

const STYLE = BnumElement.ConstructCSSStyleSheet(style);

/** Nom de famille de la police Material Symbols. */
const FONT_FAMILY = 'Material Symbols Outlined';

/** Spécification utilisée pour interroger `document.fonts`. */
const FONT_SPEC = `24px "${FONT_FAMILY}"`;

/** Délai maximum d'attente du chargement de la police avant d'abandonner. */
const FONT_LOAD_TIMEOUT_MS = 3000;

/**
 * Composant personnalisé "bnum-icon" pour afficher une icône Material Symbol.
 *
 * Ce composant permet d'afficher une icône en utilisant le nom de l'icône Material Symbol.
 * Le nom peut être défini via le contenu du slot ou via l'attribut `data-icon`.
 *
 * @example
 * <bnum-icon>home</bnum-icon>
 * <bnum-icon data-icon="search"></bnum-icon>
 *
 * @slot (default) - Nom de l'icône material symbol.
 *
 * @event {unknown} custom:element-changed:icon - Déclenché lors du changement d'icône.
 */
@Define({ tag: TAG_ICON })
export class HTMLBnumIcon extends BnumElementInternal {
	//#region Private fields
	static #_fontPromise: Promise<void> | null = null;
	#_updateScheduler: Nullable<Scheduler<string>> = null;
	//#endregion Private fields

	//#region Getter/setter
	/** Référence à la classe HTMLBnumIcon */
	@Self _!: typeof HTMLBnumIcon;

	/**
	 * Événement déclenché lors du changement d'icône. (via la propriété icon)
	 */
	@Listener(OnIconChangeInitializer, { lazy: false })
	accessor oniconchanged!: JsEvent<(newIcon: string, oldIcon: string) => void>;

	/**
	 * Nom de l'icône affichée. En lecture : contenu texte de l'élément, sinon
	 * l'attribut `data-icon`, sinon une chaîne vide. En écriture : met à jour
	 * l'affichage et déclenche `oniconchanged` si la valeur change ; une
	 * valeur `null` est ignorée, une chaîne ne respectant pas le format
	 * `[\w-]+` lève une erreur.
	 */
	get icon(): string {
		const icon =
			this.textContent?.trim?.() ||
			(this.data(DATA_ICON) as string) ||
			EMPTY_STRING;

		return icon;
	}

	set icon(value: string | null) {
		if (value !== null) {
			if (typeof value === 'string' && /^[\w-]+$/.test(value)) {
				const oldValue = this.icon;
				this.data(DATA_ICON, value);

				this.#_requestUpdateDOM(value);

				this.oniconchanged.call(value, oldValue);
			} else {
				throw new Error('Icon must be a valid string.');
			}
		}
	}
	//#endregion Getter/setter

	//#region Lifecycle
	constructor() {
		super();
	}

	/**
	 * Ajoute les feuilles de style de la police Material Symbols aux feuilles héritées.
	 */
	protected _p_getStylesheets(): CSSStyleSheet[] {
		return [...super._p_getStylesheets(), SYMBOLS, STYLE];
	}

	/**
	 * Construit le slot par défaut, affiche l'icône initiale si `data-icon` en
	 * fournit une, puis masque le composant aux technologies d'assistance
	 * (`aria-hidden`) et lance le suivi du chargement de la police tant
	 * qu'aucun `aria-hidden`/`aria-label` explicite n'est déjà présent.
	 */
	protected _p_buildDOM(container: CustomDom): void {
		container.appendChild(this._p_createSlot());

		const icon = this.data<string>(DATA_ICON);
		if (icon) this.#_updateIcon(icon);

		if (!this.hasAttribute('aria-hidden') && !this.hasAttribute('aria-label')) {
			this.setAttribute('aria-hidden', 'true');

			this.#_checkAndLoadFont();
		}
	}
	//#endregion Lifecycle

	//#region Private methods
	async #_checkAndLoadFont() {
		if (document.fonts.check(FONT_SPEC)) return;

		this._p_addState('loading');
		await this._.#_loadFont();
		this._p_removeState('loading');
	}

	/**
	 * Charge la police via l'API `FontFace` plutôt que `document.fonts.load()` :
	 * ce dernier recherche un `@font-face` déjà connu du `FontFaceSet` du
	 * document, ce qui suppose que la déclaration faite dans le shadow root de
	 * l'icône (via `adoptedStyleSheets`) s'y soit correctement propagée — un
	 * point historiquement peu fiable sur WebKit/iOS, où la promesse peut ne
	 * jamais se résoudre et bloquer l'icône en `opacity: 0` indéfiniment.
	 * Enregistrer nous-mêmes la `FontFace` sur `document.fonts` retire cette
	 * dépendance, et le timeout garantit dans tous les cas la sortie de l'état
	 * `loading` (l'icône s'affiche alors avec la police de repli le temps
	 * qu'elle finisse de charger, plutôt que de rester invisible).
	 */
	static #_loadFont(): Promise<void> {
		// Optimisation : on ne lance le chargement qu'une fois globalement
		this.#_fontPromise ??= (async () => {
			const fontFace = new FontFace(FONT_FAMILY, `url(${FONT_SRC})`, {
				weight: '200',
			});
			document.fonts.add(fontFace);

			await Promise.race([
				fontFace.load().then(
					() => undefined,
					() => undefined,
				),
				new Promise<void>(resolve => setTimeout(resolve, FONT_LOAD_TIMEOUT_MS)),
			]);
		})();

		return this.#_fontPromise;
	}

	/**
	 * Planifie une mise à jour différée de l'affichage avec le nom d'icône fourni.
	 */
	#_requestUpdateDOM(icon: string): this {
		this.#_updateScheduler ??= new Scheduler<string>(
			// eslint-disable-next-line no-shadow
			(icon: Nullable<string>) => {
				this.#_updateIcon(icon!);
			},
		);
		this.#_updateScheduler.schedule(icon);
		return this;
	}

	/**
	 * Remplace le contenu texte de l'élément par le nom de l'icône.
	 */
	#_updateIcon(icon: string): void {
		this.textContent = icon;
	}
	//#endregion Private methods

	//#region Static methods
	/**
	 * Crée une nouvelle instance de HTMLBnumIcon avec l'icône spécifiée.
	 */
	static Create(icon: string): HTMLBnumIcon {
		const element = this.EMPTY;
		element.icon = icon;
		return element;
	}

	static Write(icon: string, attribs: Record<string, string> = {}): string {
		return <HTMLBnumIcon data-icon={icon} {...attribs}></HTMLBnumIcon>;
	}

	/**
	 * Retourne un élément HTMLBnumIcon vide.
	 */
	static get EMPTY(): HTMLBnumIcon {
		return document.createElement(this.TAG) as HTMLBnumIcon;
	}

	/**
	 * Classe CSS utilisée en interne pour les icônes Material Symbols.
	 */
	static get HTML_CLASS(): string {
		return ICON_CLASS;
	}

	/**
	 * Retourne une instance de HTMLBnumIcon avec l'icône 'home'.
	 */
	static get HOME(): HTMLBnumIcon {
		return this.Create('home');
	}

	/**
	 * Retourne une instance de HTMLBnumIcon avec l'icône 'search'.
	 */
	static get SEARCH(): HTMLBnumIcon {
		return this.Create('search');
	}

	/**
	 * Retourne une instance de HTMLBnumIcon avec l'icône 'settings'.
	 */
	static get SETTINGS(): HTMLBnumIcon {
		return this.Create('settings');
	}

	/**
	 * Retourne une instance de HTMLBnumIcon avec l'icône 'person'.
	 */
	static get USER(): HTMLBnumIcon {
		return this.Create('person');
	}

	/**
	 * Retourne une instance de HTMLBnumIcon avec l'icône 'mail'.
	 */
	static get MAIL(): HTMLBnumIcon {
		return this.Create('mail');
	}

	/**
	 * Retourne une instance de HTMLBnumIcon avec l'icône 'close'.
	 */
	static get CLOSE(): HTMLBnumIcon {
		return this.Create('close');
	}

	/**
	 * Retourne une instance de HTMLBnumIcon avec l'icône 'check'.
	 */
	static get CHECK(): HTMLBnumIcon {
		return this.Create('check');
	}

	/**
	 * Retourne une instance de HTMLBnumIcon avec l'icône 'warning'.
	 */
	static get WARNING(): HTMLBnumIcon {
		return this.Create('warning');
	}

	/**
	 * Retourne une instance de HTMLBnumIcon avec l'icône 'info'.
	 */
	static get INFO(): HTMLBnumIcon {
		return this.Create('info');
	}

	/**
	 * Retourne une instance de HTMLBnumIcon avec l'icône 'delete'.
	 */
	static get DELETE(): HTMLBnumIcon {
		return this.Create('delete');
	}

	/**
	 * Retourne une instance de HTMLBnumIcon avec l'icône 'add'.
	 */
	static get ADD(): HTMLBnumIcon {
		return this.Create('add');
	}
	//#endregion Static methods
}
